import type { ComponentType } from 'react';
import React, { createElement, useMemo, useRef, useEffect, useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';

import type {
	DefinitionType,
	createLoadableComponentProps,
	LoadedModuleType,
	LoadableType,
	ActivationTrigger,
	ModuleType,
} from './types';
import { ActivationReason, ActivationTriggerType } from './types';
import { LoadingPriority } from './constants';
import { LoadableAncestorsProvider } from './LoadableAncestors';
import { usePlaceholderId } from './placeholders/usePlaceholderId';
import { makeGate, unlockGate } from './suspense/Gate';
import { Boundary } from './suspense/Boundary';

function hasDefault<P>(
	m: ModuleType<P> | { default: ModuleType<P> },
): m is { default: ModuleType<P> } {
	return typeof m === 'object' && m !== null && 'default' in m;
}

export function createSuspenseClientComponent<P extends {}>(
	{ __loadable_id__, loader, name, triggers, ssrSuccessCheckName }: DefinitionType<P>,
	{ loadableName, loadedModuleRef, displayName }: createLoadableComponentProps<P>,
) {
	const LoadableSuspenseClientComponent = (props: P) => {
		const hostRef = useRef<HTMLDivElement | null>(null);
		const observerRef = useRef<IntersectionObserver | null>(null);
		const [isInViewport, setIsInViewport] = useState(false);
		const baseId = __loadable_id__ || name || loadableName;
		const placeholderId = usePlaceholderId(baseId);
		const gate = useMemo(() => makeGate(), []);
		const loaderPromiseRef = useRef<Promise<void> | null>(null);
		const startTimeRef = useRef<number | null>(null);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const triggerCleanupRef = useRef<(() => void) | null>(null);
		const autoActivateTimeoutRef = useRef<number | null>(null);

		const renderChildren = useCallback(() => {
			if (loadedModuleRef.ref) {
				return createElement(loadedModuleRef.ref as ComponentType<P>, props);
			}
			return null;
		}, [props]);

		const onLoaded = useCallback((m: LoadedModuleType<P>) => {
			loadedModuleRef.ref = hasDefault(m) ? m.default : m;
		}, []);

		const activateComponent = useCallback(
			(reason: ActivationReason) => {
				if (!loadedModuleRef.ref && !startTimeRef.current) {
					startTimeRef.current = performance.now();
				}
				if (gate?.getStatus() === 'suspended') {
					clearTimeout(autoActivateTimeoutRef.current ?? undefined);

					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: 'loadable',
							action: 'activated',
							actionSubject: 'loadableComponent',
							attributes: {
								loadableName,
								triggers,
								placeholderId,
								activationReason: reason,
							},
						},
					}).fire();

					void unlockGate(
						gate,
						placeholderId,
						loader,
						hostRef,
						onLoaded,
						loaderPromiseRef,
						createAnalyticsEvent,
					);
				}
			},
			[gate, placeholderId, hostRef, onLoaded, createAnalyticsEvent],
		);

		const createTriggerHandlers = useCallback(
			(element: HTMLDivElement, triggerList: ActivationTrigger[]) => {
				const removers: Array<() => void> = [];
				triggerList.forEach((trigger) => {
					if (trigger.type === ActivationTriggerType.HOVER) {
						const handleHover = () => {
							activateComponent(ActivationReason.USER_ACTIVATION);
						};

						element.addEventListener('mouseenter', handleHover, { passive: true });
						removers.push(() => {
							element.removeEventListener('mouseenter', handleHover);
						});
					} else if (trigger.type === ActivationTriggerType.SCROLL_INTO_VIEW) {
						if (element && !isInViewport) {
							if (!observerRef.current) {
								observerRef.current = new IntersectionObserver(([entry]) => {
									if (entry.isIntersecting) {
										setIsInViewport(true);
										observerRef.current?.disconnect();
									}
								});
							}
							observerRef.current.observe(element);
							removers.push(() => {
								observerRef.current?.disconnect();
							});
						}
					}
				});
				return () => {
					removers.forEach((off) => off());
				};
			},
			[activateComponent, isInViewport],
		);

		const hostRefWithActivation = useCallback(
			(element: HTMLDivElement | null) => {
				hostRef.current = element;

				if (element) {
					if (triggers && triggers.length) {
						triggerCleanupRef.current = createTriggerHandlers(element, triggers);
					}
				}
			},
			[createTriggerHandlers],
		);

		useEffect(() => {
			if (isInViewport) {
				activateComponent(ActivationReason.USER_ACTIVATION);
			}
		}, [isInViewport, activateComponent]);

		useEffect(() => {
			const ms = StatsigConfigurations.getNumber('confluence_loadable_auto_activate_timeout_ms', 0);
			if (ms) {
				autoActivateTimeoutRef.current = window.setTimeout(() => {
					if (gate?.getStatus() === 'suspended') {
						activateComponent(ActivationReason.AUTO_TIMEOUT);
					}
				}, ms);
			}

			return () => {
				clearTimeout(autoActivateTimeoutRef.current ?? undefined);
				triggerCleanupRef.current?.();

				if (gate?.getStatus() === 'suspended' && triggers && triggers.length) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: 'loadable',
							action: 'unmounted',
							actionSubject: 'loadableComponent',
							attributes: {
								loadableName,
								wasEverActivated: false,
								placeholderId,
							},
						},
					}).fire();
				}
			};
		}, [activateComponent, gate, createAnalyticsEvent, placeholderId]);

		return (
			<LoadableAncestorsProvider
				id={__loadable_id__}
				name={loadableName}
				priority={LoadingPriority.ACTIVATABLE}
			>
				<div ref={hostRefWithActivation} data-hydration-shield={placeholderId}>
					<Boundary
						id={placeholderId}
						loader={loader}
						hostRef={hostRef}
						gate={gate}
						onLoaded={onLoaded}
						resolved={gate?.getStatus?.() === 'resolved'}
						render={renderChildren}
						loaderPromiseRef={loaderPromiseRef}
						loadableName={loadableName}
						ssrSuccessCheckName={ssrSuccessCheckName}
						activateComponent={activateComponent}
					/>
				</div>
			</LoadableAncestorsProvider>
		);
	};

	LoadableSuspenseClientComponent.displayName = displayName;

	return LoadableSuspenseClientComponent as LoadableType<P>;
}
